import { Button, Close } from 'components/Buttons'
import { Modal } from 'components/Modal'
import { LoadingCircle } from 'components/Loading'
import { Pagination } from 'components/Pagination'
import { SearchBar } from 'components/Search'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import config from 'config'
import { StaffMember } from 'models'
import { ReadResult } from 'interfaces/queryOptions'
import {
  list as listStaff,
  createManyStaffPermissions
} from 'modules/Staff/services/api'
import { useURLParams } from 'hooks/useURLParams'

interface Props {
  open: boolean
  setOpen: (open: boolean) => any
  clientId: string
  assignedStaffIds: string[]
}

export function CreateClientStaffPermission({
  open,
  setOpen,
  clientId,
  assignedStaffIds
}: Props) {
  const [staffIds, setStaffIds] = useState<string[]>([])
  const [data, setData] = useState<ReadResult<StaffMember>[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [search, setSearch] = useState('')

  const navigate = useNavigate()
  const query = useURLParams()
  const maxItemsPerPage = config.items_per_list_page

  const queryParams = useMemo(() => {
    const search = query.get('search')
    return { search: search ?? '' }
  }, [query])

  useEffect(() => {
    if (open) {
      setStaffIds([])
      setSearch(queryParams.search)
    }
  }, [open])

  useEffect(() => {
    setStaffIds([])
    setPage(1)
  }, [search])

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const options = {
          limit: maxItemsPerPage,
          search,
          page,
          sort: { fullname: 1 },
          ...(assignedStaffIds.length > 0 && {
            filters: [{ key: 'id', op: 'nin', value: assignedStaffIds }]
          })
        }
        const { count, data } = await listStaff(options)
        setData(data as ReadResult<StaffMember>[])
        setTotal(count)
      } catch {
        toast.error('Falha ao buscar staff members')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [maxItemsPerPage, page, search, assignedStaffIds])

  useEffect(() => {
    setLastPage(Math.ceil(total / maxItemsPerPage))
  }, [total, maxItemsPerPage])

  const onPageChange = useCallback(
    (p: number) => {
      setPage(Math.min(Math.max(1, p), lastPage))
      setStaffIds([])
    },
    [lastPage]
  )

  const onSearch = useCallback(
    (term: string) => {
      if (!term || term.length < config.min_search_length) term = ''
      term === '' ? query.delete('search') : query.set('search', term)
      navigate(
        { pathname: window.location.pathname, search: query.toString() },
        { replace: true }
      )
      setSearch(term)
    },
    [query, navigate]
  )

  const toggleStaff = useCallback((id: string) => {
    setStaffIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    )
  }, [])

  const selectAll = useCallback(() => {
    setStaffIds((prev) => {
      const allIds = data.map((s) => s.id.toString())
      return prev.length === data.length && data.length > 0 ? [] : allIds
    })
  }, [data])

  const onSave = useCallback(async () => {
    if (!clientId || staffIds.length === 0) return
    setIsSaving(true)
    try {
      // createManyStaffPermissions espera (staffId, clientIds[]) — chamamos um por um
      await Promise.all(
        staffIds.map((staffId) =>
          createManyStaffPermissions(staffId, [clientId])
        )
      )
      toast.success('Permissões adicionadas com sucesso')
      setOpen(false)
      window.location.reload()
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível adicionar as permissões'
      )
    } finally {
      setIsSaving(false)
    }
  }, [clientId, staffIds, setOpen])

  return (
    <Modal
      open={open}
      title="Adicionar staff"
      onClose={() => setOpen(false)}
      width="sm:w-1/2"
    >
      <div className="mt-4 space-y-5">
        <SearchBar value={search} onChange={onSearch} />
        {isLoading ? (
          <LoadingCircle />
        ) : (
          <div className="flex flex-col animate-fade-in-down">
            <div className="px-4 py-3">
              <span className="text-sm font-medium text-gray-700">
                Selecionados: {staffIds.length}
              </span>
            </div>
            <div className="-my-2 overflow-x-auto px-4">
              <div className="py-2 align-middle inline-block min-w-full">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-sm font-light text-gray-500 w-12">
                          <input
                            type="checkbox"
                            checked={
                              data.length > 0 &&
                              data.every((s) =>
                                staffIds.includes(s.id.toString())
                              )
                            }
                            onChange={selectAll}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-light text-gray-500">
                          NOME
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-light text-gray-500">
                          EMAIL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((staff, index) => (
                        <tr
                          key={staff.id}
                          className={
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }
                        >
                          <td className="px-4 py-3 text-center text-sm whitespace-nowrap w-12">
                            <input
                              type="checkbox"
                              checked={staffIds.includes(staff.id.toString())}
                              onChange={() => toggleStaff(staff.id.toString())}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap max-w-xxs">
                            <div className="text-xs font-semibold text-gray-900 truncate">
                              {staff.attributes.fullname ?? '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap max-w-xxs">
                            <div className="text-xs text-gray-500 truncate">
                              {staff.attributes.email ?? '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="mx-4">
              <Pagination
                currentPage={page}
                pageSize={maxItemsPerPage}
                itemsOnPage={data.length}
                totalItems={total}
                onPageChange={onPageChange}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end space-x-4">
        <Close onClick={() => setOpen(false)} />
        <Button disabled={isSaving} type="button" onClick={onSave} />
      </div>
    </Modal>
  )
}
